

<section class="api-docs">

    <h1>API Documentation</h1>

    <p>
        The RGN Load Board API allows approved users to manage their own posted loads
        and trucks using an API key.
    </p>

    <div class="notice">
        <strong>Note:</strong> Search is not currently available through the API.
        The API is currently for managing your own posted loads and trucks only.
    </div>

    <h2>API Key Setup</h2>

    <p>
        To use the API, create an API key inside your RGN Load Board account.
    </p>

    <p>
        After the API key is created, it will be shown only one time.
        Copy it and store it somewhere safe.
    </p>

    <p>
        For security reasons, RGN Load Board does not store the plain API key and
        cannot show it again later. If you lose the key, delete it and create a new one.
    </p>

    <div class="warning">
        Do not share your API key publicly. Anyone with your API key may be able to use
        the permissions assigned to that key.
    </div>

    <h2>Base URL</h2>

    <pre><code>https://api.wideandheavy.com/api/public</code></pre>

    <h2>Authentication</h2>

    <p>
        Pass your API key with every request using the <code>api_key</code> URL parameter.
    </p>

    <pre><code>GET https://api.wideandheavy.com/api/public/loads?api_key=YOUR_API_KEY</code></pre>

    <h2>HTTP Methods</h2>

    <table>
        <thead>
            <tr>
                <th>Method</th>
                <th>Purpose</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td><code>GET</code></td>
                <td>Read your posted loads or trucks</td>
            </tr>
            <tr>
                <td><code>POST</code></td>
                <td>Create a new load or truck</td>
            </tr>
            <tr>
                <td><code>PATCH</code></td>
                <td>Update, refresh, or change visibility</td>
            </tr>
            <tr>
                <td><code>DELETE</code></td>
                <td>Delete a load or truck</td>
            </tr>
        </tbody>
    </table>

    <h2>Rate Limit</h2>

    <p>Public API routes are limited to 120 requests per minute.</p>

    <pre><code>429 Too Many Requests</code></pre>

    <hr />

    <h2>Loads API</h2>

    <h3>Get Your Loads</h3>

    <pre><code>GET /loads?api_key=YOUR_API_KEY</code></pre>

    <p>Returns loads posted by your account.</p>

    <h3>Get Single Load</h3>

    <pre><code>GET /loads/&#123;load_id&#125;?api_key=YOUR_API_KEY</code></pre>

    <h3>Create Load</h3>

    <pre><code>POST /loads?api_key=YOUR_API_KEY</code></pre>

    <h4>Example Request Body</h4>

    <pre><code>&#123;
    "carrier_dot_number": 123456,
    "origin_city": "Dallas",
    "origin_state": "TX",
    "destination_city": "Phoenix",
    "destination_state": "AZ",
    "origin_start_date": "2026-07-10",
    "origin_end_date": "2026-07-12",
    "destination_start_date": "2026-07-13",
    "destination_end_date": "2026-07-15",
    "commodity": "Excavator",
    "weight": 45000,
    "length": 35,
    "width": 10,
    "height": 11,
    "well_length": 29,
    "rate": 5000,
    "cod": false,
    "permits_reimbursed": true,
    "operable": true,
    "divisible": false,
    "self_load": false,
    "self_unload": false,
    "saturday_pickup_allowed": false,
    "sunday_pickup_allowed": false,
    "saturday_delivery_allowed": false,
    "sunday_delivery_allowed": false,
    "reference": "REF-1001",
    "note": "Call before pickup"
&#125;</code></pre>

    <p>You may use ZIP instead of city and state:</p>

    <pre><code>&#123;
    "origin_zip": "75201",
    "destination_zip": "85001"
&#125;</code></pre>

    <h4>Required Load Fields</h4>

    <ul>
        <li><code>carrier_dot_number</code></li>
        <li><code>origin_city</code> and <code>origin_state</code>, or <code>origin_zip</code></li>
        <li><code>destination_city</code> and <code>destination_state</code>, or <code>destination_zip</code></li>
        <li><code>origin_start_date</code></li>
        <li><code>commodity</code></li>
        <li><code>weight</code></li>
        <li><code>length</code></li>
        <li><code>width</code></li>
        <li><code>height</code></li>
        <li><code>well_length</code></li>
    </ul>

    <h4>Load Date Rules</h4>

    <ul>
        <li>Dates cannot be in the past.</li>
        <li>Dates cannot be more than 30 days from today.</li>
        <li>Date windows cannot be more than 7 days.</li>
        <li><code>origin_end_date</code> defaults to <code>origin_start_date</code> if not provided.</li>
        <li><code>destination_start_date</code> must be after or equal to <code>origin_start_date</code>.</li>
        <li><code>destination_end_date</code> must be after or equal to <code>destination_start_date</code>.</li>
    </ul>

    <h3>Update Load</h3>

    <pre><code>PATCH /loads/&#123;load_id&#125;?api_key=YOUR_API_KEY</code></pre>

    <div class="warning">
        <strong>Important:</strong> Origin and destination locations cannot be updated after a load is created. If you need to change a location, delete the load and create a new one.
    </div>

    <p>Send only the fields you want to update.</p>

    <pre><code>&#123;
    "rate": 5500,
    "note": "Updated note",
    "visible": true
&#125;</code></pre>

    <h3>Refresh Load</h3>

    <pre><code>PATCH /loads/&#123;load_id&#125;/refresh?api_key=YOUR_API_KEY</code></pre>

    <p>Loads can only be refreshed once every 30 minutes.</p>

    <h3>Toggle Load Visibility</h3>

    <pre><code>PATCH /loads/&#123;load_id&#125;/visibility?api_key=YOUR_API_KEY</code></pre>

    <p>This switches the load between visible and hidden.</p>

    <h3>Delete Load</h3>

    <pre><code>DELETE /loads/&#123;load_id&#125;?api_key=YOUR_API_KEY</code></pre>

    <hr />

    <h2>Trucks API</h2>

    <h3>Get Your Trucks</h3>

    <pre><code>GET /trucks?api_key=YOUR_API_KEY</code></pre>

    <p>Returns trucks posted by your account.</p>

    <h3>Get Single Truck</h3>

    <pre><code>GET /trucks/&#123;truck_id&#125;?api_key=YOUR_API_KEY</code></pre>

    <h3>Create Truck</h3>

    <pre><code>POST /trucks?api_key=YOUR_API_KEY</code></pre>

    <h4>Example Request Body</h4>

    <pre><code>&#123;
    "carrier_dot_number": 123456,
    "origin_city": "Dallas",
    "origin_state": "TX",
    "available_start_date": "2026-07-10",
    "available_end_date": "2026-07-12",
    "origin_radius": 250,
    "axle_count": 7,
    "max_weight": 80000,
    "well_length": 29,
    "min_rate": 4000,
    "min_rpm": 3.5,
    "min_distance": 500,
    "tarp": false,
    "hazmat": false,
    "military_base_access": false,
    "twic": false,
    "emodal": false,
    "outriggers": false,
    "beam_trailer": false,
    "extendable_trailer": false,
    "reference": "TRUCK-1001",
    "note": "RGN available"
&#125;</code></pre>

    <p>You may use ZIP instead of city and state:</p>

    <pre><code>&#123;
    "origin_zip": "75201"
&#125;</code></pre>

    <h4>Required Truck Fields</h4>

    <ul>
        <li><code>carrier_dot_number</code></li>
        <li><code>origin_city</code> and <code>origin_state</code>, or <code>origin_zip</code></li>
        <li><code>available_start_date</code></li>
        <li><code>axle_count</code></li>
        <li><code>max_weight</code></li>
        <li><code>well_length</code></li>
    </ul>

    <h4>Truck Date Rules</h4>

    <ul>
        <li>Dates cannot be in the past.</li>
        <li>Dates cannot be more than 30 days from today.</li>
        <li>Available date window cannot be more than 7 days.</li>
        <li><code>available_end_date</code> defaults to <code>available_start_date</code> if not provided.</li>
    </ul>

    <h3>Update Truck</h3>

    <pre><code>PATCH /trucks/&#123;truck_id&#125;?api_key=YOUR_API_KEY</code></pre>

    <div class="warning">
        <strong>Important:</strong> The truck origin location cannot be updated after a truck is created. If you need to change the location, delete the truck and create a new one.
    </div>

    <p>Send only the fields you want to update.</p>

    <pre><code>&#123;
    "min_rate": 4500,
    "origin_radius": 300,
    "note": "Updated availability",
    "visible": true
&#125;</code></pre>

    <h3>Refresh Truck</h3>

    <pre><code>PATCH /trucks/&#123;truck_id&#125;/refresh?api_key=YOUR_API_KEY</code></pre>

    <p>Trucks can only be refreshed once every 30 minutes.</p>

    <h3>Toggle Truck Visibility</h3>

    <pre><code>PATCH /trucks/&#123;truck_id&#125;/visibility?api_key=YOUR_API_KEY</code></pre>

    <p>This switches the truck between visible and hidden.</p>

    <h3>Delete Truck</h3>

    <pre><code>DELETE /trucks/&#123;truck_id&#125;?api_key=YOUR_API_KEY</code></pre>

    <hr />

    <h2>Common Responses</h2>

    <h3>Success Response</h3>

    <pre><code>&#123;
    "message": "Load created successfully.",
    "data": &#123;&#125;
&#125;</code></pre>

    <h3>Validation Error</h3>

    <pre><code>&#123;
    "message": "Date window cannot be more than 7 days.",
    "errors": &#123;
        "origin_end_date": [
            "Date window cannot be more than 7 days."
        ]
    &#125;
&#125;</code></pre>

    <h3>Not Found</h3>

    <pre><code>&#123;
    "message": "Load not found",
    "data": []
&#125;</code></pre>

    <h3>Unauthorized or Not Allowed</h3>

    <pre><code>&#123;
    "message": "Carrier not found, not connected, or not allowed to post loads.",
    "data": []
&#125;</code></pre>

    <h3>Refresh Limit</h3>

    <pre><code>&#123;
    "message": "Loads can only be refreshed once every 30 minutes. You can refresh this load in 20 minutes.",
    "data": &#123;
        "load_id": 1,
        "refreshed_at": "2026-07-02T12:00:00.000000Z"
    &#125;
&#125;</code></pre>

    <hr />

    <h2>Important Notes</h2>

    <ul>
        <li>Search is not currently available through the API.</li>
        <li>The API can only manage records connected to your account.</li>
        <li>The API key must be included with every request.</li>
        <li>The API key is shown only once when created.</li>
        <li>Do not share your API key publicly.</li>
        <li>Do not place your API key in frontend browser code, public GitHub repositories, screenshots, or logs.
        </li>
        <li>Locations must match an existing location in the RGN Load Board location database.</li>
        <li>Distances and time zones are calculated automatically.</li>
        <li>Records created through the API are marked as API-created records.</li>
    </ul>
</section>
